import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { CMSUpdateModal } from './CMSUpdateModal';
import { apiClient, RegulatoryChange, DashboardStats } from '../lib/api';
import RoleTailoredDocumentCard from './RoleTailoredDocumentCard';
import {
  Filter,
  Clock,
  AlertTriangle,
  CheckCircle,
  FileText,
  Users,
  Shield,
  RefreshCw,
  Search,
  User as UserIcon
} from 'lucide-react';

interface PolicyUpdate {
  id: string;
  title: string;
  summary: string;
  department: 'Billing' | 'Finance' | 'Clinical' | 'Compliance';
  urgency: 'High' | 'Medium' | 'Low';
  timestamp: string;
  impact: string;
  status: 'New' | 'In Review' | 'Implemented';
  effectiveDate: string;
  financialImpact: string;
  affectedDepartments: string[];
  detailedChanges: string[];
  actionItems: Array<{
    id: string;
    description: string;
    dueDate: string;
    priority: 'High' | 'Medium' | 'Low';
    assignedDepartment: string;
  }>;
  complianceChecklist: Array<{
    id: string;
    task: string;
    dueDate: string;
    completed: boolean;
  }>;
  sourceDocuments: Array<{
    title: string;
    url: string;
    type: string;
  }>;
  originalData?: any; // Preserve original regulatory change data with role_tailored_content
}

export function MainDashboard({ onReportClick, searchQuery = '' }: { onReportClick?: (update: PolicyUpdate) => void; searchQuery?: string }) {
  const [selectedFilter, setSelectedFilter] = useState<string>('All');
  const [selectedUpdate, setSelectedUpdate] = useState<PolicyUpdate | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userRole, setUserRole] = useState<string>(localStorage.getItem('userRole') || '');
  
  // NEW: State for real API data
  const [regulatoryChanges, setRegulatoryChanges] = useState<RegulatoryChange[]>([]);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const roleOptions = [
    { value: '', label: 'All roles' },
    { value: 'quality_assurance', label: 'Quality Assurance Director' },
    { value: 'regulatory_compliance', label: 'Regulatory Compliance Officer' },
    { value: 'clinical', label: 'Clinical Director / Chief Medical Officer' },
    { value: 'billing', label: 'Billing Director / Revenue Cycle Manager' },
    { value: 'it_security', label: 'IT Security Officer / CISO' },
    { value: 'administration', label: 'Administrator / COO' },
    { value: 'legal', label: 'Legal Counsel / General Counsel' },
  ];

  // NEW: Load data from API on component mount
  useEffect(() => {
    loadDashboardData();
    const handleRoleChange = () => {
      setUserRole(localStorage.getItem('userRole') || '');
    };
    window.addEventListener('roleChanged', handleRoleChange);
    return () => window.removeEventListener('roleChanged', handleRoleChange);
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('📊 Loading dashboard data from API...');
      
      // Fetch stats and recent changes in parallel
      const [statsData, changesData] = await Promise.all([
        apiClient.getDashboardStats(),
        apiClient.getRecentChanges(150)
      ]);
      
      setDashboardStats(statsData);
      setRegulatoryChanges(changesData);
      
      console.log(`✅ Loaded ${changesData.length} regulatory changes`);
      console.log('📈 Dashboard stats:', statsData);
    } catch (err) {
      console.error('❌ Failed to load dashboard data:', err);
      setError('Failed to load regulatory data. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  // NEW: Convert RegulatoryChange to PolicyUpdate format for compatibility
  const convertToPolicyUpdate = (change: RegulatoryChange): PolicyUpdate => {
    // Map department based on affected departments
    const getDepartment = (): 'Billing' | 'Finance' | 'Clinical' | 'Compliance' => {
      const depts = change.affected_departments.map(d => d.toLowerCase());
      if (depts.includes('billing')) return 'Billing';
      if (depts.includes('finance')) return 'Finance';
      if (depts.includes('clinical')) return 'Clinical';
      return 'Compliance';
    };

    // Map urgency based on quality score or compliance deadline
    const getUrgency = (): 'High' | 'Medium' | 'Low' => {
      if (change.simplified_analysis.compliance_deadline) {
        const deadline = new Date(change.simplified_analysis.compliance_deadline);
        const now = new Date();
        const daysUntil = Math.floor((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        if (daysUntil < 30) return 'High';
        if (daysUntil < 90) return 'Medium';
      }

      if (change.simplified_analysis.next_steps.some(step => step.priority === 'high')) {
        return 'High';
      }

      return 'Medium';
    };

    // Determine status based on quality control
    const getStatus = (): 'New' | 'In Review' | 'Implemented' => {
      if (!change.quality_control) return 'New';
      if (change.quality_control.safe_to_use && change.quality_control.quality_score >= 8) {
        return 'Implemented';
      }
      return 'In Review';
    };

    // Calculate relative timestamp
    const getRelativeTime = (): string => {
      if (!change.analysis_timestamp) return 'Recently';

      const now = new Date();
      const timestamp = new Date(change.analysis_timestamp);
      const diffMs = now.getTime() - timestamp.getTime();
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffHours / 24);

      if (diffHours < 1) return 'Just now';
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
    };

    return {
      id: change.document_id.toString(),
      title: change.title,
      summary: change.simplified_analysis.one_sentence_summary ||
               change.simplified_analysis.detailed_summary?.substring(0, 200) + '...',
      department: getDepartment(),
      urgency: getUrgency(),
      timestamp: getRelativeTime(),
      impact: change.simplified_analysis.financial_impact
        ? `Financial impact: ${change.simplified_analysis.financial_impact.estimated_cost || 'To be determined'}`
        : 'Operational impact assessment available in source documents',
      status: getStatus(),
      effectiveDate: change.simplified_analysis.compliance_deadline || change.date || 'To be determined',
      financialImpact: change.simplified_analysis.financial_impact?.estimated_cost || 'Assessment pending',
      affectedDepartments: change.simplified_analysis.who_this_affects.departments,
      detailedChanges: change.simplified_analysis.key_requirements || [],
      actionItems: change.simplified_analysis.next_steps.map((step, idx) => ({
        id: `a${change.document_id}-${idx}`,
        description: step.action,
        dueDate: step.timeline,
        priority: (step.priority
          ? (step.priority.charAt(0).toUpperCase() + step.priority.slice(1).toLowerCase())
          : 'Medium') as 'High' | 'Medium' | 'Low',
        assignedDepartment: step.owner
      })),
      complianceChecklist: change.simplified_analysis.next_steps.slice(0, 3).map((step, idx) => ({
        id: `c${change.document_id}-${idx}`,
        task: step.action,
        dueDate: step.timeline,
        completed: false
      })),
      sourceDocuments: [{
        title: change.title,
        url: change.url,
        type: change.source
      }],
      // Preserve the original regulatory change data including role_tailored_content
      originalData: change
    };
  };

  const filters = [
    'All', 
    'Billing', 
    'Finance', 
    'Clinical', 
    'Regulatory Information',
    '---', // Separator
    'Significant', // High urgency
    'Standard',    // Medium urgency
    'General'      // Low urgency
  ];
  
  // NEW: Filter real API data by role (matches healthcare_roles/departments text)
  const filterByRole = (change: RegulatoryChange) => {
    if (!userRole) return true;
    const roleLower = userRole.toLowerCase();
    const { who_this_affects } = change.simplified_analysis;
    const roles = who_this_affects.healthcare_roles || [];
    const departments = who_this_affects.departments || [];
    const facilityTypes = who_this_affects.facility_types || [];
    return (
      roles.some((r) => r.toLowerCase().includes(roleLower)) ||
      departments.some((d) => d.toLowerCase().includes(roleLower)) ||
      facilityTypes.some((f) => f.toLowerCase().includes(roleLower))
    );
  };

  const applyFilters = (updates: PolicyUpdate[]) => {
    let filtered = (() => {
      if (selectedFilter === 'All') {
        return updates;
      }
      
      // Department filters
      if (selectedFilter === 'Regulatory Information') {
        return updates.filter(update => update.department === 'Compliance');
      }
      if (['Billing', 'Finance', 'Clinical'].includes(selectedFilter)) {
        return updates.filter(update => update.department === selectedFilter);
      }
      
      // Urgency filters
      if (selectedFilter === 'Significant') {
        return updates.filter(update => update.urgency === 'High');
      }
      if (selectedFilter === 'Standard') {
        return updates.filter(update => update.urgency === 'Medium');
      }
      if (selectedFilter === 'General') {
        return updates.filter(update => update.urgency === 'Low');
      }
      
      return updates;
    })();

    // Apply search filter on top of category/urgency filters
    if (searchQuery && searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(update => {
        const searchableText = [
          update.title,
          update.summary,
          update.department,
          update.impact,
          ...update.affectedDepartments,
          ...update.detailedChanges,
          update.financialImpact
        ].join(' ').toLowerCase();
        
        return searchableText.includes(query);
      });
    }

    return filtered;
  };

const policyUpdates = regulatoryChanges
  .filter(filterByRole)
  .map(convertToPolicyUpdate);

const filteredUpdates = applyFilters(policyUpdates);

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'High': return 'bg-red-100 text-red-800 border-red-200';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getUrgencyLabel = (urgency: string) => {
    switch (urgency) {
      case 'High': return 'Significant Information';
      case 'Medium': return 'Standard Information';
      case 'Low': return 'General Information';
      default: return 'Information';
    }
  };

  const getDepartmentColor = (department: string) => {
    switch (department) {
      case 'Billing': return 'bg-blue-100 text-blue-800';
      case 'Finance': return 'bg-purple-100 text-purple-800';
      case 'Clinical': return 'bg-green-100 text-green-800';
      case 'Compliance': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDepartmentLabel = (department: string) => {
    return department === 'Compliance' ? 'Regulatory Information' : department;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'New': return <AlertTriangle className="w-4 h-4 text-accent" />;
      case 'In Review': return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'Implemented': return <CheckCircle className="w-4 h-4 text-green-600" />;
      default: return <FileText className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'New': return 'Information Available';
      case 'In Review': return 'Professional Review Recommended';
      case 'Implemented': return 'Source Documents Ready';
      default: return 'Information Processing';
    }
  };

  const handleUpdateClick = (update: PolicyUpdate) => {
    if (onReportClick) {
      onReportClick(update);
    } else {
      setSelectedUpdate(update);
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedUpdate(null);
  };

  // NEW: Loading state
  if (loading) {
    return (
      <div className="flex-1 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading regulatory changes from backend...</p>
          <p className="text-sm text-gray-500 mt-2">Connecting to Railway API...</p>
        </div>
      </div>
    );
  }

  // NEW: Error state with retry
  if (error) {
    return (
      <div className="flex-1 p-6 flex items-center justify-center">
        <Card className="p-6 max-w-md">
          <div className="text-center">
            <AlertTriangle className="w-12 h-12 text-red-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Connection Error</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button 
              onClick={loadDashboardData}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry Connection
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Calculate real-time quality metrics from API data
  const avgQualityScore = dashboardStats?.average_quality_score ||
    (regulatoryChanges
      .filter(c => c.quality_control)
      .reduce((sum, c) => sum + (c.quality_control?.quality_score || 0), 0) /
      (regulatoryChanges.filter(c => c.quality_control).length || 1)) || 0;

  return (
    <div className="flex-1 p-6 space-y-6 overflow-auto">
      {/* Prominent Role Selector Banner */}
      <Card className="p-5 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 shadow-md">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="bg-blue-600 p-2 rounded-lg">
              <UserIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <label className="text-xs font-semibold text-blue-900 uppercase tracking-wide">
                Viewing as Role
              </label>
              <div className="flex items-center space-x-2 mt-1">
                <select
                  value={userRole}
                  onChange={(e) => {
                    const value = e.target.value;
                    setUserRole(value);
                    localStorage.setItem('userRole', value);
                    window.dispatchEvent(new Event('roleChanged'));
                  }}
                  className="px-4 py-2 border-2 border-blue-300 rounded-lg bg-white text-base font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                >
                  {roleOptions.map((role) => (
                    <option key={role.value || 'all'} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
                {userRole && (
                  <Badge className="bg-blue-600 text-white px-3 py-1 text-sm">
                    Active
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="text-right">
              <div className="text-xs text-gray-600">API Status</div>
              <div className="flex items-center space-x-1 text-sm font-semibold text-green-700">
                <CheckCircle className="w-4 h-4" />
                <span>Connected</span>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={loadDashboardData}
              className="text-xs border-blue-300 hover:bg-blue-100"
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Refresh
            </Button>
          </div>
        </div>
      </Card>

      {/* Latest CMS Updates Section */}
      <Card className="flex-1">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl">Latest Regulatory Information</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Real-time data from {dashboardStats ? Object.keys(dashboardStats.by_scraper_type).length : 0} sources
              </p>
              {searchQuery && (
                <p className="text-sm text-blue-600 mt-1 flex items-center">
                  <Search className="w-3 h-3 mr-1" />
                  Showing {filteredUpdates.length} result{filteredUpdates.length !== 1 ? 's' : ''} for "{searchQuery}"
                </p>
              )}
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <div className="flex space-x-1 flex-wrap gap-1">
                  {filters.map((filter) => {
                    // Skip rendering the separator as a button
                    if (filter === '---') {
                      return (
                        <div key="separator" className="w-px h-8 bg-gray-300 mx-1" />
                      );
                    }
                    
                    // Check if this is an urgency filter and get appropriate icon
                    const getFilterIcon = () => {
                      if (filter === 'Significant') return <AlertTriangle className="w-3 h-3 mr-1" />;
                      if (filter === 'Standard') return <Clock className="w-3 h-3 mr-1" />;
                      if (filter === 'General') return <CheckCircle className="w-3 h-3 mr-1" />;
                      return null;
                    };
                    
                    const isUrgencyFilter = ['Significant', 'Standard', 'General'].includes(filter);
                    
                    return (
                      <Button
                        key={filter}
                        variant={selectedFilter === filter ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedFilter(filter)}
                        className={`
                          ${selectedFilter === filter ? "bg-primary" : ""} 
                          ${isUrgencyFilter ? "border-orange-300" : ""}
                          flex items-center
                        `}
                      >
                        {getFilterIcon()}
                        {filter}
                      </Button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        <ScrollArea className="h-96">
          <div className="p-6 space-y-4">
            {filteredUpdates.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">No regulatory changes found for this filter</p>
                <p className="text-sm text-gray-500 mt-1">Try selecting a different category</p>
              </div>
            ) : (
              filteredUpdates.map((update) => {
                // Get the original regulatory change for full data
                const originalChange = regulatoryChanges.find(c => c.document_id.toString() === update.id);

                if (!originalChange) return null;

                // Map priority based on urgency
                const priorityMap: Record<string, 'critical' | 'high' | 'medium' | 'low'> = {
                  'High': 'high',
                  'Medium': 'medium',
                  'Low': 'low'
                };

                // Create role-tailored content from API data
                const createRoleTailoredContent = () => {
                  // Check if API provided role_tailored_content
                  if (originalChange.role_tailored_content && originalChange.role_tailored_content.length > 0) {
                    return originalChange.role_tailored_content;
                  }

                  // Otherwise create mock role-specific content
                  const baseSummary = originalChange.simplified_analysis.paragraph_summary ||
                                     originalChange.simplified_analysis.one_sentence_summary ||
                                     'No summary available';
                  const allActions = originalChange.simplified_analysis.detailed_changes ||
                                    originalChange.simplified_analysis.key_requirements || [];

                  return [
                    {
                      role: 'quality_assurance',
                      role_display_name: 'Quality Assurance Director',
                      summary: `${baseSummary}\n\nQuality Impact: This regulation requires updates to quality metrics, clinical documentation, and compliance monitoring procedures. Ensure all quality assurance protocols align with new standards.`,
                      key_actions: [
                        'Update quality metrics and monitoring protocols',
                        'Review clinical documentation requirements',
                        'Implement new compliance checkpoints',
                        ...allActions.slice(0, 2)
                      ].slice(0, 5),
                      financial_implications: undefined,
                      time_impact: originalChange.simplified_analysis.compliance_deadline
                        ? `Quality review deadline: ${new Date(originalChange.simplified_analysis.compliance_deadline).toLocaleDateString()}`
                        : 'Quality updates required within 30 days',
                      deadline: originalChange.simplified_analysis.compliance_deadline || originalChange.date
                    },
                    {
                      role: 'regulatory_compliance',
                      role_display_name: 'Regulatory Compliance Officer',
                      summary: `${baseSummary}\n\nCompliance Impact: This regulatory update requires immediate review of compliance protocols, documentation standards, and reporting requirements to ensure full regulatory alignment.`,
                      key_actions: [
                        'Review and update compliance policies',
                        'Ensure regulatory reporting readiness',
                        'Update staff training on new requirements',
                        ...allActions.slice(0, 2)
                      ].slice(0, 5),
                      financial_implications: undefined,
                      time_impact: originalChange.simplified_analysis.compliance_deadline
                        ? `Compliance deadline: ${new Date(originalChange.simplified_analysis.compliance_deadline).toLocaleDateString()}`
                        : 'Compliance review required within 30 days',
                      deadline: originalChange.simplified_analysis.compliance_deadline || originalChange.date
                    },
                    {
                      role: 'clinical',
                      role_display_name: 'Clinical Director',
                      summary: `${baseSummary}\n\nClinical Impact: This regulation affects clinical protocols, care delivery standards, and medical documentation requirements. Review and update clinical workflows accordingly.`,
                      key_actions: [
                        'Update clinical protocols and care pathways',
                        'Review medical documentation requirements',
                        'Train clinical staff on new standards',
                        ...allActions.slice(0, 2)
                      ].slice(0, 5),
                      financial_implications: undefined,
                      time_impact: originalChange.simplified_analysis.compliance_deadline
                        ? `Clinical implementation deadline: ${new Date(originalChange.simplified_analysis.compliance_deadline).toLocaleDateString()}`
                        : 'Clinical updates required within 30 days',
                      deadline: originalChange.simplified_analysis.compliance_deadline || originalChange.date
                    },
                    {
                      role: 'billing',
                      role_display_name: 'Billing Director',
                      summary: `${baseSummary}\n\nBilling Impact: This regulation may affect billing codes, reimbursement rates, and revenue cycle processes. Review financial documentation requirements and ensure compliance with payment submission deadlines.`,
                      key_actions: [
                        'Review and update billing code mappings',
                        'Verify reimbursement rate changes',
                        'Update revenue cycle procedures',
                        ...allActions.slice(0, 2)
                      ].slice(0, 5),
                      financial_implications: 'Review billing codes, reimbursement impacts, and revenue cycle changes. Update coding systems and train billing staff on new requirements.',
                      time_impact: originalChange.simplified_analysis.compliance_deadline
                        ? `Deadline: ${new Date(originalChange.simplified_analysis.compliance_deadline).toLocaleDateString()}`
                        : 'Implementation required within 30 days',
                      deadline: originalChange.simplified_analysis.compliance_deadline || originalChange.date
                    },
                    {
                      role: 'it_security',
                      role_display_name: 'IT Security Officer',
                      summary: `${baseSummary}\n\nIT Security Impact: This regulation may require updates to data security controls, system access protocols, and cybersecurity compliance measures.`,
                      key_actions: [
                        'Review data security and privacy controls',
                        'Update system access and authentication protocols',
                        'Ensure HIPAA and cybersecurity compliance',
                        ...allActions.slice(0, 2)
                      ].slice(0, 5),
                      financial_implications: undefined,
                      time_impact: originalChange.simplified_analysis.compliance_deadline
                        ? `Security review deadline: ${new Date(originalChange.simplified_analysis.compliance_deadline).toLocaleDateString()}`
                        : 'Security updates required within 30 days',
                      deadline: originalChange.simplified_analysis.compliance_deadline || originalChange.date
                    },
                    {
                      role: 'administration',
                      role_display_name: 'Administrator / COO',
                      summary: `${baseSummary}\n\nOperational Impact: This regulation requires cross-departmental coordination, resource allocation, and operational workflow updates to ensure organizational compliance.`,
                      key_actions: [
                        'Coordinate cross-departmental implementation',
                        'Allocate resources and budget for compliance',
                        'Update operational policies and procedures',
                        ...allActions.slice(0, 2)
                      ].slice(0, 5),
                      financial_implications: undefined,
                      time_impact: originalChange.simplified_analysis.compliance_deadline
                        ? `Implementation deadline: ${new Date(originalChange.simplified_analysis.compliance_deadline).toLocaleDateString()}`
                        : 'Operational updates required within 30 days',
                      deadline: originalChange.simplified_analysis.compliance_deadline || originalChange.date
                    },
                    {
                      role: 'legal',
                      role_display_name: 'Legal Counsel',
                      summary: `${baseSummary}\n\nLegal Impact: This regulation requires legal review of compliance obligations, contractual implications, and risk management considerations.`,
                      key_actions: [
                        'Review legal compliance obligations',
                        'Assess contractual and liability implications',
                        'Update risk management protocols',
                        ...allActions.slice(0, 2)
                      ].slice(0, 5),
                      financial_implications: undefined,
                      time_impact: originalChange.simplified_analysis.compliance_deadline
                        ? `Legal review deadline: ${new Date(originalChange.simplified_analysis.compliance_deadline).toLocaleDateString()}`
                        : 'Legal review required within 30 days',
                      deadline: originalChange.simplified_analysis.compliance_deadline || originalChange.date
                    }
                  ];
                };

                return (
                  <RoleTailoredDocumentCard
                    key={update.id}
                    document={{
                      id: update.id,
                      title: originalChange.title,
                      priority: priorityMap[update.urgency] || 'medium',
                      source: originalChange.source,
                      source_link: originalChange.url || originalChange.document_info?.pdf_url,
                      date: originalChange.date,
                      role_tailored_content: createRoleTailoredContent(),
                      simplified_analysis: originalChange.simplified_analysis,
                      affected_departments: originalChange.affected_departments,
                      url: originalChange.url || originalChange.document_info?.pdf_url
                    }}
                    selectedRole={userRole}
                    onClick={() => handleUpdateClick(update)}
                  />
                );
              })
            )}
          </div>
        </ScrollArea>
      </Card>

    
      {/* CMS Update Modal */}
      <CMSUpdateModal 
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        update={selectedUpdate}
      />

      {/* Infervia Disclaimer */}
      <Card className="p-4 bg-secondary/50 border-accent/20">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm mb-2">Important Notice</h4>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Infervia provides AI-powered regulatory information for research purposes only. Healthcare facilities should consult qualified compliance professionals before making operational decisions. Not a substitute for professional legal advice.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
